import { useCallback, useEffect, useState } from "react";
import { isShopConnected, shopApi } from "./shopApi";
import type { Product, ShopConfig } from "./shopApi";

interface Catalog {
  config: ShopConfig | null;
  products: Product[];
}

// 450labs caches reads for up to a minute anyway, so going back to the store
// within a minute shows the same catalog at once instead of the loading screen.
const FRESH_MS = 60_000;
let cache: { at: number; data: Catalog } | null = null;

export function useShopCatalog() {
  const [data, setData] = useState<Catalog | null>(cache?.data ?? null);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async (force: boolean) => {
    if (!isShopConnected) {
      setData({ config: null, products: [] });
      setLoading(false);
      return;
    }
    if (!force && cache && Date.now() - cache.at < FRESH_MS) {
      setData(cache.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [config, products] = await Promise.all([shopApi.getConfig(), shopApi.listProducts()]);
      cache = { at: Date.now(), data: { config, products } };
      setData(cache.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  return {
    config: data?.config ?? null,
    products: data?.products ?? [],
    loading,
    error,
    reload: () => load(true),
  };
}
